/**
 * 『シリウスの心臓』 (Heart of Sirius)
 * 作詞・作曲・編曲：傘村トータ / MV映像：UDON (VIXI)
 *
 * コンセプト：
 * - 白黒（深い漆黒の夜空と、柔らかく滲む純白の光・フィルムグレインの儚い質感）
 * - モールス信号が歌詞に使われている（「・・ ・ー・・ ーーー ・・・ー ・ ー・ーー ーーー ・・ー」＝ I LOVE YOU）
 * - 儚い（余白を活かした明朝体タイポグラフィ、消えゆく星屑、孤独なシルエット、淡い玉ボケ）
 * - 宇宙と月、星。星の瞬き（天球図・星図の幾何学軌道、クレーターが浮かぶ白い月、天の川、瞬く恒星）
 */

export class SiriusGenerator {
  constructor(p) {
    this.p = p;
    this.time = 0;
    this.compositionMode = 0; // 0: 巨大な月の弧と天球 (mv6+mv4), 1: 左下の満月と荒野の地平線 (mv1+mv2)

    // 星々・天の川・玉ボケ・垂直モールス光線
    this.stars = [];
    this.milkyWayStars = [];
    this.bokehOrbs = [];
    this.morseBeams = [];
    this.stardust = []; // 文字やクリックから零れ落ちる儚い星屑粒子
    this.pulseWaves = []; // シリウスの心臓から広がる天球波紋
    this.cursorTrail = [];

    // オフスクリーンバッファ（月の点描テクスチャ & フィルムグレイン）
    this.moonBuffer = null;
    this.grainBuffer = null;

    // マウス視差（パララックス）用
    this.targetParallaxX = 0;
    this.targetParallaxY = 0;
    this.parallaxX = 0;
    this.parallaxY = 0;

    // 心拍・モールス共鳴状態
    this.heartbeatPhase = 0;
    this.pulseBoost = 0;
    this.morseStepIndex = 0;
    this.morseStepTimer = 0;

    // 歌詞のモールス信号「I LOVE YOU」
    // ・・ (I) / ・ー・・ (L) / ーーー (O) / ・・・ー (V) / ・ (E) / ー・ーー (Y) / ーーー (O) / ・・ー (U)
    this.morseUnits = [
      { char: 'I', code: '・・', symbols: ['.', '.'] },
      { char: 'L', code: '・ー・・', symbols: ['.', '-', '.', '.'] },
      { char: 'O', code: 'ーーー', symbols: ['-', '-', '-'] },
      { char: 'V', code: '・・・ー', symbols: ['.', '.', '.', '-'] },
      { char: 'E', code: '・', symbols: ['.'] },
      { char: 'Y', code: 'ー・ーー', symbols: ['-', '.', '-', '-'] },
      { char: 'O', code: 'ーーー', symbols: ['-', '-', '-'] },
      { char: 'U', code: '・・ー', symbols: ['.', '.', '-'] }
    ];
    this.fullMorseLyric = '・・ ・ー・・ ーーー ・・・ー ・ ー・ーー ーーー ・・ー';
    this.redHint = false;
  }

  onReroll() {
    this.compositionMode = (this.compositionMode + 1) % 2;
    this.redHint = !this.redHint;
    this.pulseBoost = 1.4;
    this.moonBuffer = null; // 転生時に新しい月のクレーター模様を生成
  }

  init(params) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const density = params ? params.density : 0.5;

    this.stars = [];
    this.milkyWayStars = [];
    this.bokehOrbs = [];
    this.morseBeams = [];
    this.stardust = [];
    this.pulseWaves = [];
    this.cursorTrail = [];

    // 1. 夜空に散らばる恒星（瞬く星々）
    const starCount = Math.floor(140 + density * 220);
    for (let i = 0; i < starCount; i++) {
      const isBright = p.random() < 0.14;
      this.stars.push({
        x: p.random(-w * 0.05, w * 1.05),
        y: p.random(-h * 0.05, h * 0.88),
        size: isBright ? p.random(2.0, 4.2) : p.random(0.7, 2.0),
        baseAlpha: isBright ? p.random(170, 255) : p.random(60, 190),
        twinkleSpeed: p.random(0.015, 0.07),
        twinklePhase: p.random(p.TWO_PI),
        sharpness: p.random(1.5, 4.5), // 瞬きの鋭さ
        isBright: isBright,
        flareAngle: p.random() < 0.8 ? 0 : p.QUARTER_PI
      });
    }

    // 2. 天の川の星団（mv1.png のような帯状の微細星群）
    const milkyCount = Math.floor(260 + density * 380);
    const bandAngle = this.compositionMode === 0 ? -0.18 : -0.08;
    const bandCenterY = this.compositionMode === 0 ? h * 0.42 : h * 0.47;
    for (let i = 0; i < milkyCount; i++) {
      const along = p.random(-w * 0.6, w * 0.6);
      // ガウス分布的に帯の中心へ集める
      const spread = (p.random() + p.random() + p.random() - 1.5) * (h * 0.16);
      const mx = w * 0.5 + along * Math.cos(bandAngle) - spread * Math.sin(bandAngle);
      const my = bandCenterY + along * Math.sin(bandAngle) + spread * Math.cos(bandAngle);
      this.milkyWayStars.push({
        x: mx,
        y: my,
        size: p.random(0.5, 1.7),
        alpha: p.random(35, 165) * (1 - Math.min(1, Math.abs(spread) / (h * 0.16)) * 0.6),
        twinklePhase: p.random(p.TWO_PI),
        twinkleSpeed: p.random(0.01, 0.04)
      });
    }

    // 3. 儚く漂う玉ボケ粒子（mv5.png のソフトボケ表現）
    const bokehCount = Math.floor(14 + density * 24);
    for (let i = 0; i < bokehCount; i++) {
      this.bokehOrbs.push(this.createBokehOrb(true));
    }

    // 4. 宇宙へ昇る垂直モールス光線（mv5.png 右側の縦に連なる点と線の光線）
    const beamCount = Math.floor(3 + density * 5);
    for (let i = 0; i < beamCount; i++) {
      this.morseBeams.push(this.createMorseBeam(i, beamCount));
    }

    // 5. 地平線の山並みプロファイル（Mode 1: mv1.png 用）
    this.horizonPoints = [];
    const steps = 80;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const hx = t * w;
      const n = p.noise(t * 4.5 + 10.2) * 18 + p.noise(t * 14.0) * 5;
      // 時折小さな山稜のピークを作る
      const ridge = Math.pow(p.noise(t * 8.0 + 3.7), 3) * 22;
      this.horizonPoints.push({ x: hx, yOffset: -(n + ridge) });
    }

    // 6. 月の点描テクスチャとフィルムグレインの事前生成（キャッシュ活用）
    if (!this.moonBuffer) {
      this.generateMoonBuffer();
    }
    if (!this.grainBuffer) {
      this.generateGrainBuffer();
    }
  }

  createBokehOrb(randomY = false) {
    const p = this.p;
    const isLarge = p.random() < 0.28;
    return {
      x: p.random(p.width),
      y: randomY ? p.random(p.height) : p.height + p.random(20, 80),
      radius: isLarge ? p.random(18, 46) : p.random(4, 16),
      alpha: isLarge ? p.random(12, 38) : p.random(25, 75),
      vx: p.random(-0.12, 0.12),
      vy: p.random(-0.35, -0.08),
      phase: p.random(p.TWO_PI),
      phaseSpeed: p.random(0.01, 0.03),
      depth: isLarge ? p.random(1.2, 2.0) : p.random(0.5, 1.1)
    };
  }

  createMorseBeam(index, total) {
    const p = this.p;
    // 画面の左右（中央のタイポグラフィを邪魔しすぎない位置）を中心に配置
    let xRatio;
    if (index === 0) {
      xRatio = 0.69; // mv5.png の象徴的な右側の垂直光線位置
    } else if (index % 2 === 1) {
      xRatio = p.random(0.12, 0.36);
    } else {
      xRatio = p.random(0.64, 0.90);
    }
    return {
      x: p.width * xRatio,
      scrollOffset: p.random(1000),
      speed: p.random(0.6, 1.4),
      alpha: index === 0 ? p.random(120, 190) : p.random(45, 110),
      scale: p.random(0.8, 1.25)
    };
  }

  /**
   * MV (mv1.png, mv6.png) のような、クレーターの陰影と木炭・フィルム粒子感を持つ白い月のテクスチャを生成
   */
  generateMoonBuffer() {
    const p = this.p;
    const texSize = 360;
    this.moonBuffer = p.createGraphics(texSize, texSize);
    const g = this.moonBuffer;
    g.pixelDensity(1);
    g.loadPixels();

    const cx = texSize / 2;
    const cy = texSize / 2;
    const r = texSize * 0.46;
    const seed = p.random(1000);

    for (let y = 0; y < texSize; y++) {
      for (let x = 0; x < texSize; x++) {
        const dx = x - cx;
        const dy = y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const idx = (y * texSize + x) * 4;

        if (dist > r) {
          g.pixels[idx] = 0;
          g.pixels[idx + 1] = 0;
          g.pixels[idx + 2] = 0;
          g.pixels[idx + 3] = 0;
          continue;
        }

        // 球面座標マッピングで立体的な月の模様を作る
        const nx = dx / r;
        const ny = dy / r;
        const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));

        // 多重パーリンノイズによる「月の海（Maria）」とクレーターの濃淡
        const n1 = p.noise((nx + 1.5) * 2.2 + seed, (ny + 1.5) * 2.2 + seed, nz * 1.5);
        const n2 = p.noise((nx + 3.0) * 5.5 + seed, (ny + 3.0) * 5.5 + seed);
        const n3 = p.noise(x * 0.08 + seed, y * 0.08 + seed);

        // 月の海（暗い斑模様）のコントラスト
        const maria = n1 * 0.65 + n2 * 0.25 + n3 * 0.1;
        let surface = p.constrain(p.map(maria, 0.32, 0.70, 138, 255), 130, 255);

        // 右下〜中央が特に白く発光しているMVのライティング (mv1.png, mv6.png)
        const lightBias = p.constrain((nx * 0.25 + ny * 0.15 + nz * 0.75), 0, 1);
        surface = p.lerp(surface, 255, Math.pow(lightBias, 1.5) * 0.58);

        // MV特有のザラついた点描・フィルムグレイン質感
        const grain = (Math.random() - 0.5) * 46 * (1 - (surface / 255) * 0.45);
        const lum = p.constrain(surface + grain, 115, 255);

        // エッジのわずかな柔らかさ
        const edgeAlpha = dist > r - 2.5 ? p.map(dist, r - 2.5, r, 255, 0) : 255;

        g.pixels[idx] = lum;
        g.pixels[idx + 1] = lum;
        g.pixels[idx + 2] = lum;
        g.pixels[idx + 3] = edgeAlpha;
      }
    }
    g.updatePixels();
  }

  /**
   * MV全編にかかっているフィルムグレイン（砂嵐粒子）のタイルバッファを生成
   */
  generateGrainBuffer() {
    const p = this.p;
    const gSize = 180;
    this.grainBuffer = p.createGraphics(gSize, gSize);
    const g = this.grainBuffer;
    g.pixelDensity(1);
    g.loadPixels();
    for (let i = 0; i < gSize * gSize * 4; i += 4) {
      const v = Math.random() > 0.5 ? 255 : 0;
      const a = Math.floor(Math.random() * 18);
      g.pixels[i] = v;
      g.pixels[i + 1] = v;
      g.pixels[i + 2] = v;
      g.pixels[i + 3] = a;
    }
    g.updatePixels();
  }

  onPointerDown(x, y) {
    const p = this.p;
    const scale = Math.min(p.width, p.height) / 700;

    // クリック位置から波紋と星屑が発生し、シリウスの心臓が強く脈打つ
    this.pulseWaves.push({
      x: x,
      y: y,
      radius: 4,
      maxRadius: Math.max(p.width, p.height) * 0.75,
      alpha: 210,
      isUserClick: true
    });

    // クリック地点から小さなモールス星屑が舞い上がる
    for (let i = 0; i < 18; i++) {
      const angle = p.random(p.TWO_PI);
      const spd = p.random(0.4, 2.4) * scale;
      this.stardust.push({
        x: x + p.random(-10, 10),
        y: y + p.random(-10, 10),
        vx: Math.cos(angle) * spd,
        vy: Math.sin(angle) * spd - p.random(0.2, 0.8),
        size: p.random(1.2, 3.2),
        alpha: p.random(160, 255),
        decay: p.random(2.0, 4.5),
        isDash: p.random() < 0.35
      });
    }

    this.pulseBoost = 1.6;
  }

  onPointerMove(x, y) {
    const p = this.p;
    // 視差（パララックス）ターゲット更新
    this.targetParallaxX = ((x / p.width) - 0.5) * 28;
    this.targetParallaxY = ((y / p.height) - 0.5) * 28;

    // カーソル移動で微細な星の瞬きと「・」「ー」の残響がそっと生まれる
    if (p.frameCount % 3 === 0) {
      this.cursorTrail.push({
        x: x + p.random(-6, 6),
        y: y + p.random(-6, 6),
        vy: p.random(-0.4, -0.1),
        alpha: 150,
        size: p.random(1.2, 2.5),
        symbol: p.random() < 0.3 ? 'ー' : '・'
      });
      if (this.cursorTrail.length > 28) {
        this.cursorTrail.shift();
      }
    }
  }

  draw(params) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const scale = Math.min(w, h) / 700;

    const motion = params.motion;
    const density = params.density;
    const glow = params.glow;

    const speedMul = 0.35 + motion * 1.35;
    this.time += 0.016 * speedMul;

    // パララックスの滑らかな補間＋MV特有の微かなカメラ浮遊揺らぎ
    const camSwayX = Math.sin(this.time * 0.55) * 5 * motion;
    const camSwayY = Math.cos(this.time * 0.42) * 4 * motion;
    this.parallaxX = p.lerp(this.parallaxX, this.targetParallaxX + camSwayX, 0.06);
    this.parallaxY = p.lerp(this.parallaxY, this.targetParallaxY + camSwayY, 0.06);

    // 心拍（シリウスの心臓の点滅リズム：ドクン…ドクン…の二段鼓動）
    this.heartbeatPhase += 0.032 * (0.65 + motion * 0.9);
    const beatCycle = this.heartbeatPhase % p.TWO_PI;
    const primaryBeat = Math.exp(-Math.pow((beatCycle - 1.0) * 3.8, 2));
    const secondaryBeat = 0.6 * Math.exp(-Math.pow((beatCycle - 2.1) * 4.5, 2));
    const heartPulse = Math.min(1.5, primaryBeat + secondaryBeat + this.pulseBoost * 0.5);
    this.pulseBoost = p.lerp(this.pulseBoost, 0, 0.06);

    // モールス信号「I LOVE YOU」のステップ進行
    this.morseStepTimer += speedMul;
    if (this.morseStepTimer > 22) {
      this.morseStepTimer = 0;
      this.morseStepIndex = (this.morseStepIndex + 1) % this.morseUnits.length;
    }

    // =========================================================================
    // 1. 漆黒の宇宙背景（Deep Monochrome Space）
    // =========================================================================
    p.background(5, 5, 7);

    const ctx = p.drawingContext;

    // 夜空の深いグラデーション（地平線や天球中心のわずかな明かり）
    ctx.save();
    const skyGrad = ctx.createRadialGradient(
      w * 0.5 - this.parallaxX * 0.3,
      h * 0.42 - this.parallaxY * 0.3,
      10,
      w * 0.5,
      h * 0.5,
      w * 0.85
    );
    skyGrad.addColorStop(0, `rgba(28, 29, 34, ${0.32 * glow})`);
    skyGrad.addColorStop(0.55, 'rgba(10, 10, 13, 0.15)');
    skyGrad.addColorStop(1, 'rgba(3, 3, 5, 0)');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // =========================================================================
    // 2. 天の川（Milky Way Band）と瞬く星々（Twinkling Stars）
    // =========================================================================
    this.drawStarfield(w, h, scale, glow, speedMul);

    // =========================================================================
    // 3. 宇宙へ昇る垂直モールス光線（Vertical Morse Light Beams — mv5.png）
    // =========================================================================
    this.drawVerticalMorseBeams(w, h, scale, glow, speedMul);

    // =========================================================================
    // 4. 天球図・星図の幾何学軌道（Celestial Sphere / Astrolabe — mv2.png, mv4.png）
    // =========================================================================
    const astroX = w * 0.5 - this.parallaxX * 0.45;
    const astroY = (this.compositionMode === 0 ? h * 0.40 : h * 0.43) - this.parallaxY * 0.45;
    this.drawCelestialAstrolabe(astroX, astroY, w, h, scale, glow, heartPulse);

    // =========================================================================
    // 5. シリウスの心臓（天球の中心で点滅する恒星と脈動波）
    // =========================================================================
    this.drawSiriusHeart(astroX, astroY, scale, glow, heartPulse);

    // =========================================================================
    // 6. 白く輝く月と地平線、孤独な光のシルエット（mv1.png / mv6.png）
    // =========================================================================
    if (this.compositionMode === 0) {
      this.drawGiantMoonHorizon(w, h, scale, glow, heartPulse);
    } else {
      this.drawFullMoonLandscape(w, h, scale, glow, heartPulse);
    }

    // =========================================================================
    // 7. 儚く漂う玉ボケ粒子（Soft Defocused Bokeh Orbs — mv5.png）
    // =========================================================================
    this.drawBokehOrbs(w, h, scale, glow, speedMul, density);

    // =========================================================================
    // 8. 空間に瞬くモールス信号パルス列（歌詞テキストではなくモールス符号のみ）
    // =========================================================================
    this.drawMorseSignalHorizon(w, h, scale, glow, heartPulse);

    // =========================================================================
    // 9. 零れ落ちる星屑粒子 & カーソル軌跡 & クリック波紋
    // =========================================================================
    this.drawInteractiveElements(scale, glow, speedMul);

    // =========================================================================
    // 10. フィルムグレイン・粒子ノイズ・シネマティックビネット
    // =========================================================================
    this.drawFilmGrainAndVignette(w, h);
  }

  /**
   * 天の川と瞬く星々の描画
   */
  drawStarfield(w, h, scale, glow, speedMul) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 天の川の淡い星雲靄
    ctx.save();
    const mwY = this.compositionMode === 0 ? h * 0.42 : h * 0.47;
    const nebulaGrad = ctx.createRadialGradient(
      w * 0.5 - this.parallaxX * 0.2,
      mwY - this.parallaxY * 0.2,
      5,
      w * 0.5 - this.parallaxX * 0.2,
      mwY - this.parallaxY * 0.2,
      w * 0.42
    );
    nebulaGrad.addColorStop(0, `rgba(255, 255, 255, ${0.065 * glow})`);
    nebulaGrad.addColorStop(0.5, `rgba(220, 225, 235, ${0.025 * glow})`);
    nebulaGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = nebulaGrad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // 天の川の微細星団
    p.noStroke();
    for (let i = 0; i < this.milkyWayStars.length; i++) {
      const ms = this.milkyWayStars[i];
      ms.twinklePhase += ms.twinkleSpeed * speedMul;
      const tw = 0.55 + 0.45 * Math.sin(ms.twinklePhase);
      const a = ms.alpha * tw * glow;
      if (a < 8) continue;

      const sx = ms.x - this.parallaxX * 0.18;
      const sy = ms.y - this.parallaxY * 0.18;
      p.fill(255, 255, 255, a);
      p.circle(sx, sy, ms.size * scale);
    }

    // 夜空全体の瞬く星々
    for (let i = 0; i < this.stars.length; i++) {
      const st = this.stars[i];
      st.twinklePhase += st.twinkleSpeed * speedMul;

      // 非線形なシンチレーション（星の儚い瞬き）
      const wave = 0.5 + 0.5 * Math.sin(st.twinklePhase);
      const twinkle = 0.25 + 0.75 * Math.pow(wave, st.sharpness);
      const alpha = st.baseAlpha * twinkle * (0.55 + 0.55 * glow);

      const sx = st.x - this.parallaxX * (st.isBright ? 0.35 : 0.2);
      const sy = st.y - this.parallaxY * (st.isBright ? 0.35 : 0.2);
      const sz = st.size * scale;

      p.noStroke();
      // 明るい星の柔らかいハロー
      if (st.isBright && alpha > 60) {
        p.fill(255, 255, 255, alpha * 0.18 * glow);
        p.circle(sx, sy, sz * 4.5);
        p.fill(255, 255, 255, alpha * 0.38 * glow);
        p.circle(sx, sy, sz * 2.2);
      }

      // 星のコア
      p.fill(255, 255, 255, Math.min(255, alpha * 1.15));
      p.circle(sx, sy, sz);

      // 十字の針状光芒（瞬きのピーク時にスッと伸びる）
      if (st.isBright && twinkle > 0.55) {
        const flareLen = sz * (2.8 + twinkle * 4.5) * glow;
        p.stroke(255, 255, 255, alpha * 0.65);
        p.strokeWeight(0.65 * scale);
        p.push();
        p.translate(sx, sy);
        p.rotate(st.flareAngle);
        p.line(-flareLen, 0, flareLen, 0);
        p.line(0, -flareLen, 0, flareLen);
        p.pop();
      }
    }
  }

  /**
   * MV (mv5.png) の特徴的な「垂直に立ち昇るモールス信号の光の筋」
   */
  drawVerticalMorseBeams(w, h, scale, glow, speedMul) {
    const p = this.p;
    // I LOVE YOU の符号パターンを数値配列化 (1: dot, 3: dash, 0: gap)
    const pattern = [1, 1, 0, 1, 3, 1, 1, 0, 3, 3, 3, 0, 1, 1, 1, 3, 0, 1, 0, 3, 1, 3, 3, 0, 3, 3, 3, 0, 1, 1, 3, 0, 0];
    const unitPx = 9 * scale;
    const totalCyclePx = pattern.length * unitPx * 2.2;

    for (let b = 0; b < this.morseBeams.length; b++) {
      const beam = this.morseBeams[b];
      beam.scrollOffset += beam.speed * speedMul * 1.4;
      const bx = beam.x - this.parallaxX * 0.55;

      // 背後のごく淡い垂直ガイド光線
      p.stroke(255, 255, 255, beam.alpha * 0.14 * glow);
      p.strokeWeight(0.6 * scale);
      p.line(bx, h * 0.08, bx, h * 0.88);

      // 点（・）と線（ー）の光のパルスが下から上へ昇る
      let curY = h * 0.90 - (beam.scrollOffset % totalCyclePx);
      while (curY < h * 0.92) {
        curY += totalCyclePx;
      }

      for (let rep = 0; rep < 3; rep++) {
        let py = curY - rep * totalCyclePx;
        for (let i = 0; i < pattern.length; i++) {
          const sym = pattern[i];
          if (sym > 0 && py > h * 0.08 && py < h * 0.88) {
            // 上端・下端でフェードするエンベロープ
            const vFade = Math.sin(p.constrain(p.map(py, h * 0.08, h * 0.88, 0, p.PI), 0, p.PI));
            const pulseAlpha = beam.alpha * vFade * glow;

            if (sym === 1) {
              // 短点（・）：輝く光の粒
              p.noStroke();
              p.fill(255, 255, 255, pulseAlpha * 0.35);
              p.circle(bx, py, 5.5 * scale * beam.scale);
              p.fill(255, 255, 255, Math.min(255, pulseAlpha * 1.2));
              p.circle(bx, py, 2.2 * scale * beam.scale);
            } else if (sym === 3) {
              // 長点（ー）：垂直の細い光の線
              const dashLen = unitPx * 1.6 * beam.scale;
              p.stroke(255, 255, 255, pulseAlpha * 0.3);
              p.strokeWeight(3.0 * scale);
              p.line(bx, py - dashLen * 0.5, bx, py + dashLen * 0.5);
              p.stroke(255, 255, 255, Math.min(255, pulseAlpha * 1.15));
              p.strokeWeight(1.2 * scale);
              p.line(bx, py - dashLen * 0.5, bx, py + dashLen * 0.5);
            }
          }
          py -= (sym === 3 ? unitPx * 2.4 : unitPx * 1.6);
        }
      }
    }
  }

  /**
   * MV (mv2.png, mv4.png) の象徴的な天球図・星図の幾何学ライン（Astrolabe / Celestial Sphere）
   */
  drawCelestialAstrolabe(cx, cy, w, h, scale, glow, heartPulse) {
    const p = this.p;
    const ctx = p.drawingContext;
    const maxR = Math.max(w, h) * 0.72;
    const baseAlpha = (0.38 + 0.35 * glow + heartPulse * 0.12);

    p.push();
    p.translate(cx, cy);

    // 1. 中心を真横に貫く天球基準線（mv2.png, mv4.png の水平ライン）
    p.stroke(255, 255, 255, 115 * baseAlpha);
    p.strokeWeight(1.1 * scale);
    p.line(-w, 0, w, 0);

    // 2. 放射状の経線（実線と破線が交互に広がる天球経線 — mv4.png）
    const rotSlow = this.time * 0.035;
    const numRays = 24;
    for (let i = 0; i < numRays; i++) {
      const angle = (p.TWO_PI / numRays) * i + rotSlow;
      const isDashed = i % 2 === 1;

      ctx.save();
      if (isDashed) {
        ctx.setLineDash([4 * scale, 6 * scale]);
        p.stroke(255, 255, 255, 70 * baseAlpha);
        p.strokeWeight(0.75 * scale);
      } else {
        p.stroke(255, 255, 255, 48 * baseAlpha);
        p.strokeWeight(0.6 * scale);
      }
      const innerStart = (i % 4 === 0 ? 12 : 36) * scale;
      p.line(
        Math.cos(angle) * innerStart,
        Math.sin(angle) * innerStart,
        Math.cos(angle) * maxR,
        Math.sin(angle) * maxR
      );
      ctx.restore();
    }

    // 3. 同心円の緯線リング群（実線・二重線・かすれた部分円弧）
    p.noFill();
    const ringRadii = [0.09, 0.16, 0.24, 0.33, 0.44, 0.56, 0.68, 0.82];
    ringRadii.forEach((rRatio, idx) => {
      const r = maxR * rRatio;
      const ringAlpha = (idx === 4 || idx === 6 ? 125 : 65) * baseAlpha;
      p.stroke(255, 255, 255, ringAlpha);
      p.strokeWeight((idx === 4 || idx === 6 ? 1.2 : 0.65) * scale);
      p.circle(0, 0, r * 2);

      // 二重リング（mv4.png の外周二重円環）
      if (idx === 4 || idx === 6) {
        const r2 = r + 7 * scale;
        p.stroke(255, 255, 255, ringAlpha * 0.85);
        p.strokeWeight(0.8 * scale);
        p.circle(0, 0, r2 * 2);
      }

      // 経線と緯線の交点に光る小さな星ドット（mv2.png, mv4.png）
      if (idx >= 2 && idx <= 6) {
        p.noStroke();
        p.fill(255, 255, 255, 140 * baseAlpha);
        for (let j = 0; j < numRays; j += 2) {
          const a = (p.TWO_PI / numRays) * j + rotSlow;
          p.circle(Math.cos(a) * r, Math.sin(a) * r, 2.4 * scale);
        }
        p.noFill();
      }
    });

    // 4. MV (mv4.png) の象徴的な「内向き目盛り付き天球リング（Graduated Tick Arc）」
    const tickRingR = maxR * 0.44;
    const tickInnerR = tickRingR - 11 * scale;
    p.stroke(255, 255, 255, 115 * baseAlpha);
    p.strokeWeight(0.9 * scale);
    // 下半分の内側アーク
    p.arc(0, 0, tickInnerR * 2, tickInnerR * 2, 0, p.PI);
    // 等間隔の目盛り線（ティックマーク）
    const numTicks = 60;
    for (let t = 0; t <= numTicks; t++) {
      const ang = (p.PI / numTicks) * t;
      const isMajor = t % 5 === 0;
      const tLen = (isMajor ? 11 : 6.5) * scale;
      p.strokeWeight((isMajor ? 1.0 : 0.6) * scale);
      p.line(
        Math.cos(ang) * tickRingR,
        Math.sin(ang) * tickRingR,
        Math.cos(ang) * (tickRingR - tLen),
        Math.sin(ang) * (tickRingR - tLen)
      );
    }

    // 5. モールス信号「I LOVE YOU」が刻まれた回転軌道リング（内側＆外側の二重軌道）
    const morseRadii = [maxR * 0.285, maxR * 0.55];
    for (let ringIdx = 0; ringIdx < morseRadii.length; ringIdx++) {
      const morseRingR = morseRadii[ringIdx];
      const dir = ringIdx === 0 ? -1 : 1;
      let curAng = -p.HALF_PI + this.time * 0.08 * dir;
      for (let rep = 0; rep < 2; rep++) {
        for (let u = 0; u < this.morseUnits.length; u++) {
          const unit = this.morseUnits[u];
          const isCurrentActive = (u === this.morseStepIndex) && (rep === 0);
          const symAlpha = (isCurrentActive ? 245 : 115) * baseAlpha;

          for (let s = 0; s < unit.symbols.length; s++) {
            const sym = unit.symbols[s];
            if (sym === '.') {
              p.noStroke();
              p.fill(255, 255, 255, symAlpha);
              p.circle(
                Math.cos(curAng) * morseRingR,
                Math.sin(curAng) * morseRingR,
                (isCurrentActive ? 3.8 : 2.3) * scale
              );
              curAng += 0.032;
            } else {
              p.noFill();
              p.stroke(255, 255, 255, symAlpha);
              p.strokeWeight((isCurrentActive ? 2.0 : 1.1) * scale);
              p.arc(0, 0, morseRingR * 2, morseRingR * 2, curAng, curAng + 0.065);
              curAng += 0.095;
            }
          }
          curAng += 0.065; // 符号間スペース
        }
        curAng += 0.18; // フレーズ間スペース
      }
    }

    // 6. 天球を横切る大円アーク（mv2.png, mv4.png の中心に接する大きな弧と上部の交差円）
    p.noFill();
    // (a) 中心 (0,0) に頂点が接し、画面下部へ大きく広がる美しい白の大円弧 (mv4.png)
    const lowerArcR = maxR * 0.92;
    p.stroke(255, 255, 255, 165 * baseAlpha);
    p.strokeWeight(1.8 * scale);
    p.arc(0, lowerArcR, lowerArcR * 2, lowerArcR * 2, p.PI * 1.15, p.PI * 1.85);

    // 下部の二重同心アーク (mv4.png 下部)
    const bottomArcR = maxR * 0.62;
    p.stroke(255, 255, 255, 130 * baseAlpha);
    p.strokeWeight(1.3 * scale);
    p.arc(0, maxR * 0.82, bottomArcR * 2, bottomArcR * 2, p.PI * 1.18, p.PI * 1.82);
    p.strokeWeight(0.8 * scale);
    p.arc(0, maxR * 0.82, (bottomArcR - 9 * scale) * 2, (bottomArcR - 9 * scale) * 2, p.PI * 1.18, p.PI * 1.82);

    // (b) 上部で交差する天球の双子円環 (mv2.png, mv4.png 上部)
    const upperCircleR = maxR * 0.23;
    p.stroke(255, 255, 255, 135 * baseAlpha);
    p.strokeWeight(1.3 * scale);
    p.circle(0, -upperCircleR * 1.15, upperCircleR * 2);
    p.stroke(255, 255, 255, 80 * baseAlpha);
    p.strokeWeight(0.8 * scale);
    p.circle(0, -upperCircleR * 1.55, upperCircleR * 2.1);

    // 7. 再生成（Reroll）で切り替わる天球上部の儚い紅〜琥珀の星（mv2.png 上部）
    if (this.redHint) {
      const redX = maxR * 0.14;
      const redY = -maxR * 0.36;
      const redPulse = 0.6 + 0.4 * heartPulse;
      ctx.save();
      const rg = ctx.createRadialGradient(redX, redY, 1, redX, redY, 22 * scale);
      rg.addColorStop(0, `rgba(255, 230, 220, ${0.95 * redPulse})`);
      rg.addColorStop(0.35, `rgba(235, 90, 75, ${0.65 * redPulse})`);
      rg.addColorStop(1, 'rgba(200, 40, 30, 0)');
      ctx.fillStyle = rg;
      ctx.beginPath();
      ctx.arc(redX, redY, 22 * scale, 0, p.TWO_PI);
      ctx.fill();
      ctx.restore();
    }

    p.pop();
  }

  /**
   * 天球の中心で点滅する「シリウスの心臓」（mv4.png 中心の繊細な光の星核と波紋）
   */
  drawSiriusHeart(cx, cy, scale, glow, heartPulse) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 心拍のピークで新しい天球波紋を生む
    if (heartPulse > 0.92 && p.frameCount % 18 === 0) {
      this.pulseWaves.push({
        x: cx,
        y: cy,
        radius: 8 * scale,
        maxRadius: Math.min(p.width, p.height) * 0.48,
        alpha: 135 * glow,
        isUserClick: false
      });
    }

    // 中心のソフトハロー（明滅）
    const glowRadius = (42 + heartPulse * 38) * scale * (0.7 + 0.5 * glow);

    ctx.save();
    const coreGrad = ctx.createRadialGradient(cx, cy, 1, cx, cy, glowRadius);
    if (this.redHint) {
      // 転生モードに応じて白光の中にほんのりと赤い鼓動が宿る
      coreGrad.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
      coreGrad.addColorStop(0.22, `rgba(255, 210, 205, ${0.65 * glow})`);
      coreGrad.addColorStop(0.55, `rgba(220, 75, 70, ${0.22 * glow * heartPulse})`);
      coreGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    } else {
      coreGrad.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
      coreGrad.addColorStop(0.25, `rgba(245, 248, 255, ${0.55 * glow})`);
      coreGrad.addColorStop(0.6, `rgba(220, 225, 235, ${0.16 * glow})`);
      coreGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    }
    ctx.fillStyle = coreGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, glowRadius, 0, p.TWO_PI);
    ctx.fill();
    ctx.restore();

    // mv4.png 中心のような微細な光の粒子クラスター（シリウスの心臓の核）
    p.push();
    p.translate(cx, cy);
    p.noStroke();
    const clusterRadius = (14 + heartPulse * 8) * scale;
    for (let i = 0; i < 28; i++) {
      const a = i * 2.39996 + this.time * 0.4; // 黄金角スパイラル配置
      const r = Math.sqrt(i / 28) * clusterRadius;
      const flicker = 0.5 + 0.5 * Math.sin(this.time * 5 + i);
      p.fill(255, 255, 255, (180 - i * 4) * flicker * glow);
      p.circle(Math.cos(a) * r, Math.sin(a) * r, (2.4 - (i / 28) * 1.4) * scale);
    }

    // シリウスの一等星十字フレア
    const flareLen = (28 + heartPulse * 32) * scale * glow;
    p.stroke(255, 255, 255, 200 * glow);
    p.strokeWeight(1.1 * scale);
    p.line(-flareLen, 0, flareLen, 0);
    p.line(0, -flareLen * 0.85, 0, flareLen * 0.85);

    p.fill(255, 255, 255, 255);
    p.noStroke();
    p.circle(0, 0, (5.5 + heartPulse * 3.0) * scale);
    p.pop();
  }

  /**
   * 構図モード 0: MV (mv6.png) の「画面下部に広がる巨大な白い月の地平線と、その頂にぽつんと立つ光のシルエット」
   */
  drawGiantMoonHorizon(w, h, scale, glow, heartPulse) {
    const p = this.p;
    const ctx = p.drawingContext;

    const moonR = w * 0.68;
    const moonCX = w * 0.5 - this.parallaxX * 0.7;
    const moonCY = h * 0.80 + moonR - h * 0.04 - this.parallaxY * 0.6;
    const moonTopY = moonCY - moonR;

    // 1. 月の地平線から宇宙へ滲む白い大気ハロー（Bloom）
    ctx.save();
    const haloGrad = ctx.createRadialGradient(
      moonCX,
      moonCY,
      moonR * 0.92,
      moonCX,
      moonCY,
      moonR * (1.16 + 0.05 * glow)
    );
    haloGrad.addColorStop(0, `rgba(255, 255, 255, ${0.85 * glow})`);
    haloGrad.addColorStop(0.35, `rgba(240, 242, 248, ${0.28 * glow})`);
    haloGrad.addColorStop(0.7, `rgba(200, 205, 215, ${0.07 * glow})`);
    haloGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = haloGrad;
    ctx.fillRect(0, h * 0.52, w, h * 0.48);
    ctx.restore();

    // 2. 月本体の描画（事前生成したクレーター点描テクスチャ）
    if (this.moonBuffer) {
      p.push();
      p.imageMode(p.CENTER);
      // テクスチャは 0.92 倍の半径を持つため補正して描画
      const drawSize = (moonR * 2) / 0.92;
      p.image(this.moonBuffer, moonCX, moonCY, drawSize, drawSize);
      p.pop();
    }

    // 月の中央上部の眩しいホワイトアウト光（mv6.png の頂点付近の強い白光）
    ctx.save();
    const topGlow = ctx.createRadialGradient(
      moonCX,
      moonTopY + moonR * 0.14,
      5,
      moonCX,
      moonTopY + moonR * 0.14,
      moonR * 0.48
    );
    topGlow.addColorStop(0, `rgba(255, 255, 255, ${0.85 * glow})`);
    topGlow.addColorStop(0.5, `rgba(255, 255, 255, ${0.32 * glow})`);
    topGlow.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = topGlow;
    ctx.beginPath();
    ctx.arc(moonCX, moonCY, moonR, 0, p.TWO_PI);
    ctx.fill();
    ctx.restore();

    // 3. 月の頂にぽつんと佇む、白く発光する孤独なシルエット（mv6.png）
    this.drawGlowingSilhouette(moonCX, moonTopY + 2 * scale, scale * 0.85, glow, heartPulse);
  }

  /**
   * 構図モード 1: MV (mv1.png) の「左下に浮かぶ大きな白い満月と、黒い荒野の稜線、佇む光のシルエット」
   */
  drawFullMoonLandscape(w, h, scale, glow, heartPulse) {
    const p = this.p;
    const ctx = p.drawingContext;

    const horizonY = h * 0.77 - this.parallaxY * 0.7;
    const moonX = w * 0.20 - this.parallaxX * 0.55;
    const moonY = horizonY - h * 0.11;
    const moonR = Math.min(w, h) * 0.175;

    // 1. 地平線背後の水平の白い微光（mv1.png の地平線上のほのかな光帯）
    ctx.save();
    const hGrad = ctx.createLinearGradient(0, horizonY - 55 * scale, 0, horizonY + 20 * scale);
    hGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
    hGrad.addColorStop(0.65, `rgba(240, 242, 248, ${0.22 * glow})`);
    hGrad.addColorStop(0.88, `rgba(255, 255, 255, ${0.48 * glow})`);
    hGrad.addColorStop(1, 'rgba(10, 10, 12, 1)');
    ctx.fillStyle = hGrad;
    ctx.fillRect(0, horizonY - 55 * scale, w, 75 * scale);
    ctx.restore();

    // 2. 満月のハロー（柔らかい白い滲み）
    ctx.save();
    const mHalo = ctx.createRadialGradient(moonX, moonY, moonR * 0.8, moonX, moonY, moonR * (1.85 + 0.3 * glow));
    mHalo.addColorStop(0, `rgba(255, 255, 255, ${0.85 * glow})`);
    mHalo.addColorStop(0.4, `rgba(245, 247, 252, ${0.26 * glow})`);
    mHalo.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = mHalo;
    ctx.beginPath();
    ctx.arc(moonX, moonY, moonR * 2.2, 0, p.TWO_PI);
    ctx.fill();
    ctx.restore();

    // 3. 満月本体の描画（クレーター点描テクスチャ）
    if (this.moonBuffer) {
      p.push();
      p.imageMode(p.CENTER);
      const drawSize = (moonR * 2) / 0.92;
      p.image(this.moonBuffer, moonX, moonY, drawSize, drawSize);
      p.pop();
    }

    // 4. 黒い荒野・山並みの地平線シルエット（mv1.png）
    p.noStroke();
    p.fill(6, 6, 8);
    p.beginShape();
    p.vertex(0, h);
    for (let i = 0; i < this.horizonPoints.length; i++) {
      const pt = this.horizonPoints[i];
      p.vertex(pt.x, horizonY + pt.yOffset * scale);
    }
    p.vertex(w, h);
    p.endShape(p.CLOSE);

    // 5. 月の少し右の地平線上に佇む、白い光を纏ったシルエット（mv1.png）
    const figX = moonX + moonR * 1.48;
    const figY = horizonY + 8 * scale;
    this.drawGlowingSilhouette(figX, figY, scale * 1.15, glow, heartPulse);
  }

  /**
   * MV (mv1.png, mv6.png) に描かれている、白い光に包まれた小さな少女の立ち姿シルエット
   */
  drawGlowingSilhouette(x, y, s, glow, heartPulse) {
    const p = this.p;
    const ctx = p.drawingContext;
    const figH = 38 * s;

    p.push();
    p.translate(x, y);

    // 周囲の淡いオーラ（白いブルーム）
    ctx.save();
    const aura = ctx.createRadialGradient(0, -figH * 0.55, 1, 0, -figH * 0.55, figH * 0.95);
    aura.addColorStop(0, `rgba(255, 255, 255, ${(0.75 + heartPulse * 0.15) * glow})`);
    aura.addColorStop(0.45, `rgba(240, 245, 255, ${0.28 * glow})`);
    aura.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = aura;
    ctx.beginPath();
    ctx.arc(0, -figH * 0.55, figH * 0.95, 0, p.TWO_PI);
    ctx.fill();
    ctx.restore();

    // 細い脚のシルエット
    p.stroke(230, 235, 245, 210);
    p.strokeWeight(1.2 * s);
    p.line(-1.6 * s, -figH * 0.32, -2.0 * s, 0);
    p.line(1.6 * s, -figH * 0.32, 2.2 * s, 0);

    // 風に揺れる髪とコート・ドレスの白い光シルエット
    const wind = Math.sin(this.time * 1.8) * 1.6 * s;
    p.noStroke();
    p.fill(255, 255, 255, 240);
    p.beginShape();
    p.vertex(0, -figH * 0.85); // 頭頂
    p.bezierVertex(4.5 * s, -figH * 0.82, 5.5 * s + wind, -figH * 0.55, 6.5 * s + wind, -figH * 0.30); // 背〜裾右
    p.vertex(-5.5 * s, -figH * 0.30); // 裾左
    p.bezierVertex(-4.5 * s, -figH * 0.55, -4.0 * s, -figH * 0.82, 0, -figH * 0.85);
    p.endShape(p.CLOSE);

    // 頭部
    p.circle(0, -figH * 0.88, 6.8 * s);

    p.pop();
  }

  /**
   * MV (mv5.png) の儚く漂う玉ボケ（Soft Defocused Bokeh Orbs）
   */
  drawBokehOrbs(w, h, scale, glow, speedMul, density) {
    const p = this.p;
    const ctx = p.drawingContext;

    for (let i = 0; i < this.bokehOrbs.length; i++) {
      const orb = this.bokehOrbs[i];
      orb.x += orb.vx * speedMul;
      orb.y += orb.vy * speedMul;
      orb.phase += orb.phaseSpeed * speedMul;

      if (orb.y < -orb.radius * 2) {
        Object.assign(orb, this.createBokehOrb(false));
      }
      if (orb.x < -orb.radius * 2) orb.x = w + orb.radius;
      if (orb.x > w + orb.radius * 2) orb.x = -orb.radius;

      const ox = orb.x - this.parallaxX * orb.depth;
      const oy = orb.y - this.parallaxY * orb.depth;
      const r = orb.radius * scale;
      const breathe = 0.55 + 0.45 * Math.sin(orb.phase);
      const a = (orb.alpha / 255) * breathe * glow;
      if (a < 0.01) continue;

      ctx.save();
      const grad = ctx.createRadialGradient(ox, oy, r * 0.1, ox, oy, r);
      grad.addColorStop(0, `rgba(255, 255, 255, ${Math.min(0.85, a * 1.3)})`);
      grad.addColorStop(0.68, `rgba(245, 247, 250, ${a * 0.85})`);
      grad.addColorStop(0.88, `rgba(235, 238, 245, ${a * 0.45})`);
      grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(ox, oy, r, 0, p.TWO_PI);
      ctx.fill();
      ctx.restore();
    }
  }

  /**
   * 空間に瞬くモールス信号「・・ ・ー・・ ーーー ・・・ー ・ ー・ーー ーーー ・・ー」（歌詞文字ではなくモールス符号のみを静かに脈動表示）
   */
  drawMorseSignalHorizon(w, h, scale, glow, heartPulse) {
    const p = this.p;
    const ctx = p.drawingContext;

    const px = -this.parallaxX * 0.65;
    const py = -this.parallaxY * 0.65;
    const rowY = (this.compositionMode === 0 ? h * 0.22 : h * 0.26) + py;
    const fontSize = Math.max(10, Math.min(w, h) * 0.019);

    ctx.save();
    ctx.shadowColor = `rgba(255, 255, 255, ${0.65 * glow})`;
    ctx.shadowBlur = (8 + heartPulse * 5) * scale * glow;

    p.textFont("'Shippori Mincho', 'Noto Serif JP', serif");
    p.textSize(fontSize);
    p.textAlign(p.CENTER, p.CENTER);
    p.noStroke();

    const totalUnits = this.morseUnits.length;
    const spanW = w * 0.74;
    const startX = (w - spanW) * 0.5 + px;

    for (let u = 0; u < totalUnits; u++) {
      const unit = this.morseUnits[u];
      const ux = startX + (spanW / (totalUnits - 1)) * u;
      const isCurrent = u === this.morseStepIndex;
      const subtleFlicker = 0.82 + 0.18 * Math.sin(this.time * 2.8 + u * 0.9);
      const uAlpha = (isCurrent ? 235 : 125) * subtleFlicker * glow;

      p.fill(255, 255, 255, Math.min(255, uAlpha));
      p.text(unit.code, ux, rowY);

      // アクティブなモールス符号の上に小さな恒星の閃きを灯す
      if (isCurrent) {
        p.fill(255, 255, 255, Math.min(255, uAlpha * 0.85));
        p.circle(ux, rowY - 13 * scale, (3.0 + heartPulse * 1.5) * scale);
      }
    }

    ctx.restore();
  }

  /**
   * 零れ落ちる星屑粒子・カーソル軌跡・波紋の描画
   */
  drawInteractiveElements(scale, glow, speedMul) {
    const p = this.p;

    // 1. 儚い星屑粒子（Stardust）
    p.noStroke();
    for (let i = this.stardust.length - 1; i >= 0; i--) {
      const sd = this.stardust[i];
      sd.x += sd.vx * speedMul;
      sd.y += sd.vy * speedMul;
      sd.alpha -= sd.decay * speedMul;
      if (sd.alpha <= 0) {
        this.stardust.splice(i, 1);
        continue;
      }
      if (sd.isDash) {
        p.stroke(255, 255, 255, sd.alpha * glow);
        p.strokeWeight(1.3 * scale);
        p.line(sd.x - 4 * scale, sd.y, sd.x + 4 * scale, sd.y);
        p.noStroke();
      } else {
        p.fill(255, 255, 255, sd.alpha * glow);
        p.circle(sd.x, sd.y, sd.size * scale);
      }
    }

    // 2. カーソル移動のモールス＆星屑トレイル
    p.textFont("'Shippori Mincho', 'Noto Serif JP', serif");
    p.textSize(Math.max(10, 12 * scale));
    p.textAlign(p.CENTER, p.CENTER);
    for (let i = this.cursorTrail.length - 1; i >= 0; i--) {
      const tr = this.cursorTrail[i];
      tr.y += tr.vy * speedMul;
      tr.alpha -= 3.2 * speedMul;
      if (tr.alpha <= 0) {
        this.cursorTrail.splice(i, 1);
        continue;
      }
      p.fill(255, 255, 255, tr.alpha * glow);
      p.text(tr.symbol, tr.x, tr.y);
    }

    // 3. シリウスの鼓動波・クリック波紋
    p.noFill();
    for (let i = this.pulseWaves.length - 1; i >= 0; i--) {
      const pw = this.pulseWaves[i];
      pw.radius += (pw.isUserClick ? 4.2 : 2.2) * scale * speedMul;
      pw.alpha -= (pw.isUserClick ? 2.6 : 1.5) * speedMul;
      if (pw.alpha <= 0 || pw.radius > pw.maxRadius) {
        this.pulseWaves.splice(i, 1);
        continue;
      }
      p.stroke(255, 255, 255, pw.alpha * glow);
      p.strokeWeight((pw.isUserClick ? 1.2 : 0.75) * scale);
      p.circle(pw.x, pw.y, pw.radius * 2);
    }
  }

  /**
   * MV全体の空気感を決定づけるフィルムグレイン（粒子ノイズ）とビネット
   */
  drawFilmGrainAndVignette(w, h) {
    const p = this.p;
    const ctx = p.drawingContext;

    // 1. フィルムグレインのランダムオフセット重ね合わせ
    if (this.grainBuffer) {
      const gSize = this.grainBuffer.width;
      const ox = -Math.floor(p.random(gSize));
      const oy = -Math.floor(p.random(gSize));
      for (let y = oy; y < h; y += gSize) {
        for (let x = ox; x < w; x += gSize) {
          p.image(this.grainBuffer, x, y);
        }
      }
    }

    // 2. 古いフィルムのような微細な白いチリ（ダスト）が時折かすかに瞬く
    if (p.random() < 0.35) {
      p.noStroke();
      p.fill(255, 255, 255, p.random(25, 65));
      p.circle(p.random(w), p.random(h), p.random(0.8, 1.8));
    }

    // 3. 四隅を静かに引き締めるビネット（Vignette）
    ctx.save();
    const vig = ctx.createRadialGradient(w * 0.5, h * 0.5, w * 0.32, w * 0.5, h * 0.5, w * 0.75);
    vig.addColorStop(0, 'rgba(0, 0, 0, 0)');
    vig.addColorStop(0.75, 'rgba(0, 0, 0, 0.22)');
    vig.addColorStop(1, 'rgba(0, 0, 0, 0.62)');
    ctx.fillStyle = vig;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }
}
